import { useState, useEffect, memo } from 'react';
import s3Service from '../services/s3Service';

// Global cache for photo URLs to avoid redundant S3 calls
const photoCache = new Map();
const pendingRequests = new Map();

/**
 * ResidentAvatar Component
 * Fetches and displays resident photo from S3 using Cognito Identity Pool
 * Falls back to initials if photo is not available
 * 
 * PERFORMANCE OPTIMIZED:
 * - Uses React.memo to prevent unnecessary re-renders
 * - Global cache to prevent duplicate S3 calls for same photo
 * - Request deduplication for concurrent requests
 */
const ResidentAvatar = memo(function ResidentAvatar({ 
  photoS3Key, 
  residentName, 
  severity = 'MINOR',
  size = 40 
}) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchPhoto = async () => {
      if (!photoS3Key || photoS3Key.trim() === '') {
        setLoading(false);
        return;
      }

      // Check cache first
      if (photoCache.has(photoS3Key)) {
        const cached = photoCache.get(photoS3Key);
        if (isMounted) {
          setPhotoUrl(cached.url);
          setError(cached.error);
          setLoading(false);
        }
        return;
      }

      // Check if request is already pending
      if (pendingRequests.has(photoS3Key)) {
        try {
          const result = await pendingRequests.get(photoS3Key);
          if (isMounted) {
            setPhotoUrl(result.url);
            setError(result.error);
            setLoading(false);
          }
        } catch (err) {
          if (isMounted) {
            setError(true);
            setLoading(false);
          }
        }
        return;
      }

      // Create new request
      const requestPromise = (async () => {
        try {
          const url = await s3Service.getPhotoUrl(photoS3Key);
          const result = { url, error: false };
          photoCache.set(photoS3Key, result);
          return result;
        } catch (err) {
          console.error('[ResidentAvatar] Failed to fetch photo:', err);
          const result = { url: null, error: true };
          photoCache.set(photoS3Key, result);
          return result;
        } finally {
          pendingRequests.delete(photoS3Key);
        }
      })();

      pendingRequests.set(photoS3Key, requestPromise);

      try {
        const result = await requestPromise;
        if (isMounted) {
          setPhotoUrl(result.url);
          setError(result.error);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchPhoto();

    return () => {
      isMounted = false;
    };
  }, [photoS3Key]);

  const getInitials = () => {
    if (!residentName) return '??';
    return residentName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getSeverityColor = () => {
    if (severity === 'SEVERE') return "linear-gradient(135deg, #ff6b6b 0%, #d63031 100%)";
    if (severity === 'MODERATE') return "linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)";
    return "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)";
  };

  // Show initials while loading or if no photo
  if (loading || !photoUrl || error) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: getSeverityColor(),
          border: "2px solid #ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: size * 0.35,
          fontWeight: 700,
          color: "#ffffff",
        }}
      >
        {getInitials()}
      </div>
    );
  }

    // Show photo if loaded successfully
  return (
    <img
      src={photoUrl}
      alt={residentName}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
        border: "2px solid #ffffff",
        userSelect: "none",
        pointerEvents: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
      }}
      onError={() => setError(true)}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      draggable={false}
    />
  );
});

export default ResidentAvatar;
